import {Component, DestroyRef, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {from, isObservable, Observable} from 'rxjs';
import {finalize, take} from 'rxjs/operators';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {I18nMessage} from '@app/shared/api/model/i18n';
import {TableColumn} from "@shared/components/ace-table/ace-table.component";
import {ArrayUtils} from "@shared/util/collections/collection-utils";

export enum DismissReason {CANCELLED = 'CANCELLED', IGNORED = 'IGNORED', DESTROYED = 'DESTROYED'}

export type ConfirmFn = (() => any | Observable<any>);

export enum UIMessageType {
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warning',
  DANGER = 'danger',
  PRIMARY = 'primary'
}

export interface UIMessage {
  message?: I18nMessage;
  text?: string;
  type?: UIMessageType;
}

export interface ModalTableConfig {
  tableColumns: TableColumn[];
  tableRows: any[];
}

// Callers never get the modal ref back, so closing the dialog from outside has to go through here.
// The two triggers are independent and can both apply. If a component already has ngUnsubscribe,
// pass that as trigger$ rather than injecting a DestroyRef and ending up with two teardown mechanisms.
export interface DismissTriggers {
  destroyRef?: DestroyRef;
  trigger$?: Observable<unknown>;
}

export interface ModalOptions {
  title?: I18nMessage;
  header?: I18nMessage;
  items?: I18nMessage[];
  body?: string | string[] | I18nMessage | I18nMessage[];
  messageTemplate?: TemplateRef<any>;
  messages?: UIMessage[];
  yesText?: I18nMessage;
  noText?: I18nMessage;
  confirmFn?: ConfirmFn;
  afterConfirmFn?: (result?: any) => void;
  cancelFn?: (reason?: any) => void;
  dismissOn?: DismissTriggers;
  hideCancelButton?: boolean;
  modalSize?: 'sm' | 'md' | 'lg' | 'xl' | string;
  tableConfig?: ModalTableConfig;
}

@Component({
  selector: 'app-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss'],
  standalone: false
})
export class ConfirmationComponent implements OnInit {
  private readonly DEFAULT_CONFIRM_YES_TEXT = {messageKey: 'Yes'};
  private readonly DEFAULT_CONFIRM_NO_TEXT = {messageKey: 'No'};
  private readonly logger: AceLogger;

  @Input() onConfirmFn: ConfirmFn;
  title: I18nMessage;
  header: I18nMessage;
  items: I18nMessage[];
  body: string | string[] | I18nMessage | I18nMessage[];
  messages: UIMessage[];
  messageTemplate: TemplateRef<any>;
  confirmYesText: I18nMessage;
  cancelText: I18nMessage;
  doingAction = false;
  hideCancelButton: boolean;
  bodyMessages: (string | I18nMessage)[] = [];
  tableConfig: ModalTableConfig;

  @ViewChild('cellTemplate', {static: true}) cellTemplate: TemplateRef<any>;
  @ViewChild('headerTemplate', {static: true}) headerTemplate: TemplateRef<any>;

  constructor(public activeModal: NgbActiveModal, logService: AceLoggerService) {
    this.logger = logService.create(ConfirmationComponent.name);
  }

  static createModal(modalService: NgbModal, modalOptions: ModalOptions): Observable<any> {
    const modalRef = modalService.open(ConfirmationComponent, {
      size: modalOptions.modalSize || 'lg',
      centered: true
    });
    const modalInstance: ConfirmationComponent = modalRef.componentInstance;
    modalInstance.title = modalOptions.title;
    modalInstance.header = modalOptions.header;
    modalInstance.body = modalOptions.body;
    modalInstance.items = modalOptions.items;
    modalInstance.messages = modalOptions.messages;
    modalInstance.messageTemplate = modalOptions.messageTemplate;
    modalInstance.onConfirmFn = modalOptions.confirmFn;
    modalInstance.tableConfig = modalOptions.tableConfig;
    if (modalOptions.yesText) {
      modalInstance.confirmYesText = modalOptions.yesText;
    }
    if (modalOptions.noText) {
      modalInstance.cancelText = modalOptions.noText;
    }
    modalInstance.hideCancelButton = modalOptions.hideCancelButton;
    // Dismissing rejects modalRef.result, so the caller's cancelFn runs for these triggers too. Once
    // the dialog has gone both have nothing left to dismiss, so they are released rather than left
    // holding the modal until the caller is destroyed.
    const dismissAsDestroyed = () => modalRef.dismiss(DismissReason.DESTROYED);
    const unregisterOnDestroy = modalOptions.dismissOn?.destroyRef?.onDestroy(dismissAsDestroyed);
    const dismissSubscription = modalOptions.dismissOn?.trigger$?.pipe(take(1)).subscribe(dismissAsDestroyed);
    return from(modalRef.result.then((result: any) => {
      if (modalOptions.afterConfirmFn) {
        modalOptions.afterConfirmFn(result);
      }
    }, (dismissed) => {
      if (modalOptions.cancelFn) {
        modalOptions.cancelFn(dismissed);
      }
    }).finally(() => {
      unregisterOnDestroy?.();
      dismissSubscription?.unsubscribe();
    }));
  }

  ngOnInit() {
    if (!this.confirmYesText) {
      this.confirmYesText = this.DEFAULT_CONFIRM_YES_TEXT;
    }
    if (!this.cancelText) {
      this.cancelText = this.DEFAULT_CONFIRM_NO_TEXT;
    }
    this.bodyMessages = this.body != null ? ArrayUtils.asArray(this.body) : null;
    this.tableConfig = this.inflateTableColumnConfigs(this.tableConfig ?? null);
  }

  onAction() {
    if (this.onConfirmFn != null && this.onConfirmFn instanceof Function) {
      const confirmFnResult: any = this.onConfirmFn();
      if (confirmFnResult != null && isObservable(confirmFnResult)) {
        this.doingAction = true;
        confirmFnResult.pipe(finalize(() => this.doingAction = false), take(1)).subscribe((observableResult) => {
          this.activeModal.close(observableResult);
        });
      } else {
        this.activeModal.close(confirmFnResult);
      }
    } else {
      this.activeModal.close();
    }
  }

  onCancel() {
    this.activeModal.dismiss(DismissReason.CANCELLED);
  }

  onIgnore() {
    this.activeModal.dismiss(DismissReason.IGNORED);
  }

  private inflateTableColumnConfigs(tableConfig: ModalTableConfig | null): ModalTableConfig | null {
    return tableConfig
      ? {
        tableRows: tableConfig.tableRows,
        tableColumns: tableConfig.tableColumns.map(tableColumn => ({
          headerTemplate: this.headerTemplate,
          cellTemplate: this.cellTemplate,
          ...tableColumn,
        }))
      }
      : null;
  }
}
